/*
 * Cart drawer: volume-discount layout helpers (used by sections/cart-drawer.liquid).
 *
 * - Swaps [data-cd-live] blocks right away on cart:change. theme.js only swaps the drawer body
 *   1.25s after a quantity change, which would leave the tier bar and prices lagging.
 * - -/+ stepper and Kaching tier buttons set the line's quantity input and fire "change". The
 *   theme's <line-item-quantity> then posts /cart/change.js and re-renders the drawer.
 * - <cd-bundle>, <cd-bundle-banner>, <kaching-price-hint>: Kaching Bundles tiers, read live.
 *   The loader is shared as window.cdKaching (used by assets/card-pack-selector.js).
 * - <cd-ship-countdown>: time left until the same-day DHL cutoff, plus the arrival date.
 */
(() => {
  if (window.__cdVolumeLoaded) return;
  window.__cdVolumeLoaded = true;

  const drawer = () => document.getElementById('cart-drawer');
  const sectionId = () => drawer()?.closest('.shopify-section')?.id.replace('shopify-section-', '');

  /* ---------- Live blocks ---------- */
  document.addEventListener('cart:change', (event) => {
    const html = event.detail?.cart?.sections?.[sectionId()];
    const root = drawer();
    if (!html || !root || !event.detail.cart.item_count) return;

    const fresh = new DOMParser().parseFromString(html, 'text/html');
    root.querySelectorAll('[data-cd-live]').forEach((block) => {
      const next = fresh.querySelector(`[data-cd-live="${block.dataset.cdLive}"]`);
      if (next) block.replaceWith(document.importNode(next, true));
    });

    // Shopify gives a line a new key whenever its discount changes (e.g. a Kaching tier), so copy
    // the new key onto the existing row now instead of waiting for the delayed body swap
    root.querySelectorAll('line-item[data-variant-id]').forEach((line) => {
      const next = fresh.querySelector(`line-item[data-variant-id="${line.dataset.variantId}"]`);
      const input = line.querySelector('input[data-line-key]');
      const nextInput = next?.querySelector('input[data-line-key]');
      if (!input || !nextInput) return;
      input.dataset.lineKey = nextInput.dataset.lineKey;
      input.value = input.defaultValue = nextInput.value;
      const remove = line.querySelector('.cd-line__remove');
      const nextRemove = next.querySelector('.cd-line__remove');
      if (remove && nextRemove) remove.href = nextRemove.getAttribute('href');
    });
  });

  // theme.js looks the changed line up by its old key. After a tier change that key no longer
  // exists, so it reports quantity 0 and animates the row away until the body re-renders
  // (the "disappears for a moment" flicker). Correct the quantity from the variant instead.
  document.addEventListener('line-item:change', (event) => {
    const line = event.target.closest?.('#cart-drawer line-item[data-variant-id]');
    if (!line || event.detail?.quantity !== 0) return;
    const item = event.detail.cart?.items?.find((cartItem) => String(cartItem.variant_id) === line.dataset.variantId);
    if (item) event.detail.quantity = item.quantity;
  }, true);

  /* ---------- Stepper and pack buttons ---------- */
  const pending = new WeakMap();

  const commit = (input, quantity, delay) => {
    const max = input.max ? parseInt(input.max, 10) : Infinity;
    input.value = Math.max(0, Math.min(quantity, max));
    clearTimeout(pending.get(input));
    pending.set(input, setTimeout(() => input.dispatchEvent(new Event('change', { bubbles: true })), delay));
  };

  document.addEventListener('click', (event) => {
    const button = event.target.closest?.('#cart-drawer [data-cd-step], #cart-drawer [data-cd-set]');
    if (!button) return;
    const input = button.closest('line-item-quantity')?.querySelector('input[data-line-key]');
    if (!input) return;

    if (button.dataset.cdSet) {
      if (parseInt(input.value, 10) === parseInt(button.dataset.cdSet, 10)) return;
      button.parentElement.querySelectorAll('.cd-tier').forEach((b) => b.classList.toggle('is-active', b === button));
      commit(input, parseInt(button.dataset.cdSet, 10), 0);
    } else {
      // Debounced so quick taps on + become one request
      commit(input, (parseInt(input.value, 10) || 0) + parseInt(button.dataset.cdStep, 10), 400);
    }
  });

  /* ---------- Age gate ---------- */
  document.addEventListener('change', (event) => {
    if (event.target.id !== 'cart-age-verify') return;
    document.querySelectorAll('#cart-drawer [data-age-gated]').forEach((button) => { button.disabled = !event.target.checked; });
  });

  /* ---------- Discount code ---------- */
  document.addEventListener('submit', (event) => {
    const form = event.target.closest?.('[data-cd-discount-form]');
    if (!form) return;
    event.preventDefault();
    const code = form.elements.code.value.trim();
    if (!code) return;
    window.location.href = `${form.getAttribute('action')}${encodeURIComponent(code)}?redirect=${encodeURIComponent(window.location.pathname)}`;
  });

  const escapeHtml = (text) => String(text).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

  /* ---------- Kaching Bundles deals ----------
   * Kaching only prints its deal JSON on product pages, so we read it from a bare alternate
   * template (templates/product.kaching-data.liquid) and cache it per product for 30 min.
   * The tiers are display only: Kaching's discount function applies the real price per line.
   */
  const KACHING_TTL = 30 * 60 * 1000;
  const kachingMemory = new Map();
  const numericId = (id) => String(id ?? '').replace(/\D/g, '');

  const parseDeals = (html) => {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const deals = [];
    doc.querySelectorAll('script.kaching-bundles-deal-block-settings').forEach((script) => {
      try {
        const deal = JSON.parse(script.textContent);
        const bars = (deal.dealBars || [])
          .filter((bar) => bar.dealBarType === 'quantity-break' && bar.quantity > 0)
          .map((bar) => ({ quantity: bar.quantity, type: bar.discountType, value: Number(bar.discountValue) || 0, label: bar.label || '' }))
          .sort((a, b) => a.quantity - b.quantity);
        if (bars.length < 2) return;
        deals.push({
          variant: deal.abTestVariantNumber || 1,
          discountName: deal.discountName,
          visibility: deal.blockVisibility,
          selectedProductIds: (deal.selectedProductIds || []).map(numericId),
          selectedCollectionIds: (deal.selectedCollectionIds || []).map(numericId),
          excludedProductIds: (deal.excludedProductIds || []).map(numericId),
          excludedCollectionIds: (deal.excludedCollectionIds || []).map(numericId),
          bars
        });
      } catch (e) {}
    });
    // A/B variants of one deal: use variant A
    return deals.sort((a, b) => a.variant - b.variant);
  };

  const loadDeals = (handle) => {
    if (!handle) return Promise.resolve([]);
    if (kachingMemory.has(handle)) return kachingMemory.get(handle);

    let cached = null;
    try {
      cached = JSON.parse(sessionStorage.getItem(`cd-kaching:${handle}`));
    } catch (e) {}
    if (cached && Date.now() - cached.time < KACHING_TTL) {
      const promise = Promise.resolve(cached.deals);
      promise.sync = cached.deals;
      kachingMemory.set(handle, promise);
      return promise;
    }

    const promise = fetch(`${window.Shopify?.routes?.root || '/'}products/${handle}?view=kaching-data`)
      .then((response) => (response.ok ? response.text() : ''))
      .then((html) => {
        const deals = parseDeals(html);
        promise.sync = deals;
        try {
          sessionStorage.setItem(`cd-kaching:${handle}`, JSON.stringify({ time: Date.now(), deals }));
        } catch (e) {}
        return deals;
      })
      .catch(() => []);
    kachingMemory.set(handle, promise);
    return promise;
  };

  const dealApplies = (deal, productId, collectionIds) => {
    if (deal.excludedProductIds.includes(productId) || collectionIds.some((id) => deal.excludedCollectionIds.includes(id))) return false;
    if (deal.visibility === 'all-products') return true;
    if (!deal.selectedProductIds.length && !deal.selectedCollectionIds.length) return true;
    return deal.selectedProductIds.includes(productId) || collectionIds.some((id) => deal.selectedCollectionIds.includes(id));
  };

  const pickDeal = (deals, element) => {
    const productId = numericId(element.dataset.productId);
    const collectionIds = (element.dataset.collectionIds || '').split(',').map(numericId).filter(Boolean);
    return deals.find((deal) => dealApplies(deal, productId, collectionIds)) || null;
  };

  const barSaving = (bar) => (bar.type === 'percentage' && bar.value > 0 ? `${bar.value}%` : bar.type === 'default' ? '' : bar.label);

  const formatMoney = (cents) => {
    const format = window.themeVariables?.settings?.moneyFormat || '€{{amount_with_comma_separator}}';
    const value = (cents / 100).toFixed(2);
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (match, key) => {
      const [whole, decimals] = value.split('.');
      const grouped = (separator) => whole.replace(/\B(?=(\d{3})+(?!\d))/g, separator);
      switch (key) {
        case 'amount_with_comma_separator': return `${grouped('.')},${decimals}`;
        case 'amount_no_decimals': return grouped(',');
        case 'amount_no_decimals_with_comma_separator': return grouped('.');
        default: return `${grouped(',')}.${decimals}`;
      }
    });
  };

  // Tier buttons + "add X more" hint for one cart line
  class CdBundle extends HTMLElement {
    connectedCallback() {
      const promise = loadDeals(this.dataset.handle);
      // Render straight away from cache, so the drawer re-render doesn't flicker
      if (promise.sync) this.render(promise.sync);
      else promise.then((deals) => this.isConnected && this.render(deals));
    }

    render(deals) {
      const deal = pickDeal(deals, this);
      if (!deal || !deal.bars.some((bar) => barSaving(bar))) return;

      const quantity = parseInt(this.dataset.quantity, 10) || 0;
      const current = [...deal.bars].reverse().find((bar) => bar.quantity <= quantity);
      const next = deal.bars.find((bar) => bar.quantity > quantity);
      const texts = this.closest('[data-bundle-next]')?.dataset || {};

      const buttons = deal.bars.map((bar) => {
        const saving = barSaving(bar);
        const classes = ['cd-tier', bar === current ? 'is-active' : '', bar.quantity <= quantity ? 'is-unlocked' : ''].join(' ');
        return `<button type="button" class="${classes}" data-cd-set="${bar.quantity}" aria-label="${bar.quantity}×${saving ? ` −${escapeHtml(saving.replace(/^save\s*/i, ''))}` : ''}">
          ${bar.quantity}×${saving ? `<small>-${escapeHtml(saving.replace(/^save\s*/i, ''))}</small>` : ''}
        </button>`;
      }).join('');

      let hint = '';
      if (next && barSaving(next)) {
        const from = current ? current.quantity : 0;
        const progress = Math.round(((quantity - from) / (next.quantity - from)) * 100);
        const count = next.quantity - quantity;
        hint = `<p class="cd-bundle__hint">${(texts.bundleNext || 'Add [count] more to save [percent]')
          .replace('[count]', `<strong>${count}</strong>`)
          .replace('[percent]', `<strong>${escapeHtml(barSaving(next))}</strong>`)}</p>
          <div class="cd-bundle__bar"><span style="width: ${Math.max(4, progress)}%"></span></div>`;
      } else if (current && barSaving(current)) {
        hint = `<p class="cd-bundle__hint">${(texts.bundleMax || 'Best bundle price unlocked ([percent] off)')
          .replace('[percent]', `<strong>${escapeHtml(barSaving(current))}</strong>`)}</p>`;
      }

      this.innerHTML = `<div class="cd-tiers" role="group" aria-label="Bundle size" style="--cd-tier-count: ${Math.min(deal.bars.length, 4)}">${buttons}</div>${hint}`;
      this.hidden = false;
    }
  }

  // Kaching rounds the discounted unit price up to the cent (3.85 at -15% = 3.28)
  const unitPrice = (price, bar) => (bar?.type === 'percentage' && bar.value > 0 ? Math.ceil(price * (1 - bar.value / 100) - 1e-6) : price);

  // All cards share the deals loaded for the first card, then check each card's visibility
  const loadCardDeals = (handle) => {
    if (!window.__cdKachingReferenceHandle) window.__cdKachingReferenceHandle = handle;
    return loadDeals(window.__cdKachingReferenceHandle);
  };

  // "from €x/can at 30+" on product cards (best Kaching tier)
  class KachingPriceHint extends HTMLElement {
    connectedCallback() {
      loadCardDeals(this.dataset.handle).then((deals) => {
        const deal = pickDeal(deals, this);
        const best = deal && [...deal.bars].reverse().find((bar) => bar.type === 'percentage' && bar.value > 0);
        const price = parseInt(this.dataset.price, 10);
        if (!best || !price || !this.isConnected) return;
        this.innerHTML = escapeHtml(this.dataset.text || 'from [price]/can at [qty]+')
          .replace('[price]', `<strong>${formatMoney(unitPrice(price, best))}</strong>`)
          .replace('[qty]', best.quantity);
        this.hidden = false;
      });
    }
  }

  // Shared with assets/card-pack-selector.js
  window.cdKaching = { loadCardDeals, pickDeal, unitPrice, formatMoney, escapeHtml };
  document.dispatchEvent(new CustomEvent('cd-kaching:ready'));

  // "Buy more of the same can and save up to 15%" banner at the top of the drawer
  class CdBundleBanner extends HTMLElement {
    connectedCallback() {
      loadDeals(this.dataset.handle).then((deals) => {
        const deal = pickDeal(deals, this);
        const percentages = deal ? deal.bars.filter((bar) => bar.type === 'percentage').map((bar) => bar.value) : [];
        const best = percentages.length ? Math.max(...percentages) : 0;
        if (!best || !this.isConnected) return;
        this.innerHTML = this.dataset.text.replace('[percent]', `<strong>${best}%</strong>`);
        this.hidden = false;
      });
    }
  }

  /* ---------- Shipping countdown ---------- */
  const WEEKDAYS = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };

  class CdShipCountdown extends HTMLElement {
    connectedCallback() {
      this.render();
      this._timer = setInterval(() => this.render(), 30000);
    }

    disconnectedCallback() {
      clearInterval(this._timer);
    }

    // Wall-clock time in the warehouse timezone
    nowInZone() {
      try {
        const parts = Object.fromEntries(new Intl.DateTimeFormat('en-US', {
          timeZone: this.dataset.tz, weekday: 'short', year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric', hourCycle: 'h23'
        }).formatToParts(new Date()).map((p) => [p.type, p.value]));
        return { y: +parts.year, m: +parts.month, d: +parts.day, weekday: WEEKDAYS[parts.weekday], minutes: +parts.hour * 60 + +parts.minute };
      } catch (e) {
        return null;
      }
    }

    render() {
      const now = this.nowInZone();
      const [cutH, cutM] = (this.dataset.cutoff || '14:00').split(':').map((n) => parseInt(n, 10) || 0);
      if (!now) return;

      const cutoff = cutH * 60 + cutM;
      // Czech public holidays (warehouse in CZ): fixed dates + Good Friday / Easter Monday, no dispatch on these days
      const easterSunday = (y) => {
        const a = y % 19, b = Math.floor(y / 100), c = y % 100, d = Math.floor(b / 4), e = b % 4, f = Math.floor((b + 8) / 25);
        const g = Math.floor((b - f + 1) / 3), h = (19 * a + b - d - g + 15) % 30, i = Math.floor(c / 4), k = c % 4;
        const l = (32 + 2 * e + 2 * i - h - k) % 7, m = Math.floor((a + 11 * h + 22 * l) / 451);
        return new Date(Date.UTC(y, Math.floor((h + l - 7 * m + 114) / 31) - 1, ((h + l - 7 * m + 114) % 31) + 1));
      };
      const isHoliday = (date) => {
        const md = `${date.getUTCMonth() + 1}-${date.getUTCDate()}`;
        if (['1-1', '5-1', '5-8', '7-5', '7-6', '9-28', '10-28', '11-17', '12-24', '12-25', '12-26'].includes(md)) return true;
        const easter = easterSunday(date.getUTCFullYear()).getTime();
        const day = 24 * 60 * 60 * 1000;
        return date.getTime() === easter - 2 * day || date.getTime() === easter + day;
      };
      const isBusinessDay = (date) => date.getUTCDay() !== 0 && date.getUTCDay() !== 6 && !isHoliday(date);
      const addBusinessDays = (date, days) => {
        const result = new Date(date);
        while (days > 0) {
          result.setUTCDate(result.getUTCDate() + 1);
          if (isBusinessDay(result)) days--;
        }
        return result;
      };

      const today = new Date(Date.UTC(now.y, now.m - 1, now.d));
      const shipsToday = isBusinessDay(today) && now.minutes < cutoff;
      // Only show while same-day dispatch is still possible; after the cutoff, on weekends and holidays it disappears
      // after the cutoff / weekend / holiday: show the next dispatch day (data-later-text); without that text hide
      if (!shipsToday && !this.dataset.laterText) {
        this.innerHTML = '';
        this.hidden = true;
        return;
      }
      let shipDate = today;
      if (!shipsToday) {
        shipDate = addBusinessDays(today, 1);
      }

      const fmt = (date) => date.toLocaleDateString(this.dataset.locale || 'en-GB', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' });
      const min = parseInt(this.dataset.min, 10) || 1;
      const max = Math.max(min, parseInt(this.dataset.max, 10) || min);
      const arriveMin = addBusinessDays(shipDate, min);
      const arriveMax = addBusinessDays(shipDate, max);
      const dateHtml = `<strong>${min === max ? fmt(arriveMin) : `${fmt(arriveMin)}–${fmt(arriveMax)}`}</strong>`;

      let text;
      if (shipsToday) {
        const left = cutoff - now.minutes;
        const time = `<strong>${Math.floor(left / 60) ? `${Math.floor(left / 60)} h ` : ''}${left % 60} min</strong>`;
        text = (this.dataset.todayText || 'Order within [time], ships today with DHL. Arrives [date]').replace('[time]', time);
      } else {
        const tomorrow = new Date(today);
        tomorrow.setUTCDate(tomorrow.getUTCDate() + 1);
        const dayNames = (this.dataset.days || '').split(',');
        const shipDay = shipDate.getTime() === tomorrow.getTime()
          ? (this.dataset.tomorrow || 'tomorrow')
          : (dayNames.length === 7 ? dayNames[shipDate.getUTCDay()] : `on ${shipDate.toLocaleDateString('en-GB', { weekday: 'long', timeZone: 'UTC' })}`);
        text = (this.dataset.laterText || 'Ships [shipday] with DHL. Arrives [date]').replace('[shipday]', shipDay);
      }

      this.innerHTML = text.replace('[date]', dateHtml);
      this.hidden = false;
    }
  }

  if (!customElements.get('cd-bundle')) customElements.define('cd-bundle', CdBundle);
  if (!customElements.get('cd-bundle-banner')) customElements.define('cd-bundle-banner', CdBundleBanner);
  if (!customElements.get('kaching-price-hint')) customElements.define('kaching-price-hint', KachingPriceHint);
  if (!customElements.get('cd-ship-countdown')) customElements.define('cd-ship-countdown', CdShipCountdown);
})();
